import React from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Heading,
  Text,
  Skeleton,
  useColorModeValue,
  Button,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'

import { DataRecord } from '../types'
import { formatDate } from '../utils/helpers'
import CollapsibleList from './CollapsibleList'

interface DataTableProps {
  data: DataRecord[]
  isLoading: boolean
  error: unknown
  showUserId?: boolean
  recordType?: 'Data' | 'Alpha'
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading,
  error,
  showUserId = false,
  recordType = 'Data',
}) => {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const tableBg = useColorModeValue('white', 'gray.800')

  const handleRowClick = (id: number) => {
    navigate(`/record/data/${id}`)
  }

  const getSymbols = (record: DataRecord): string[] => {
    if (record.symbol_data && Object.keys(record.symbol_data).length > 0) {
      return Object.keys(record.symbol_data)
    }
    return record.target_asset ? [record.target_asset] : []
  }

  const getFeatures = (record: DataRecord): string[] => {
    if (record.symbol_data) {
      const firstSymbol = Object.values(record.symbol_data)[0]
      if (firstSymbol?.features) {
        return firstSymbol.features.map(f => f.name)
      }
    }
    return []
  }

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
        <Button
          mt={6}
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer borderRadius="md" boxShadow="sm" bg={tableBg}>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Name</Th>
              {showUserId && <Th>Owner Id</Th>}
              <Th>Symbols</Th>
              <Th>Features</Th>
              <Th>Encrypted</Th>
              <Th>Last Updated</Th>
              {/* <Th>Created At</Th> */}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  {showUserId && (
                    <Td>
                      <Skeleton height="20px" />
                    </Td>
                  )}
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                </Tr>
              ))
            ) : data.length > 0 ? (
              data.map((record) => {
                const symbols = getSymbols(record)
                const features = getFeatures(record)

                return (
                  <Tr
                    key={record.id}
                    _hover={{ bg: hoverBg, cursor: 'pointer' }}
                    onClick={() => handleRowClick(record.id)}
                  >
                    <Td>{record.id}</Td>
                    <Td fontWeight="medium">{record.name}</Td>
                    {showUserId && <Td>{record.user_id}</Td>}
                    <Td>
                      <Wrap spacing={1}>
                        {symbols.map((symbol) => (
                          <WrapItem key={symbol}>
                            <Badge
                              colorScheme="blue"
                              fontSize="xs"
                              px={2}
                              py={1}
                            >
                              {symbol}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Td>
                    <Td>
                      <CollapsibleList
                        items={features?.map((f) => f) || []}
                      />
                    </Td>
                    <Td>
                      <Badge
                        colorScheme="gray"
                        fontSize="xs"
                        p={1}
                      >
                        {record.is_encrypted ? 'Encrypted' : 'Unencrypted'}
                      </Badge>
                    </Td>
                    <Td>{formatDate(record.last_updated)}</Td>
                    {/* <Td>{formatDate(record.created_at)}</Td> */}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={8} textAlign="center" py={6}>
                  <Text color="gray.500">
                    No {recordType.toLowerCase()} records found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default DataTable
